import { Eval } from '@app/evaluation-core/types'
import React from 'react'
import { useState } from 'react'
import * as S from '../styled'
import {
  FaCheckCircle,
  FaExclamation,
  FaExclamationCircle,
  FaFolderOpen,
} from 'react-icons/fa'
import { getStatusText } from '../helpers'
import { ActivityIndicator } from 'react-native'
import { navigate } from '@app/navigation'
import { RatingTemplate } from '../../../types/Templates'
import { useAxios } from '@common/hooks/useAxios'
import { SearchClubData } from '../types'
import ConfirmDialogV2 from '@app/ui/confirm-dialog/ConfirmDialogV2'

interface EvaluationItemProps {
  evaluation: Eval & { courseName?: string }
  club: SearchClubData
  isAdminUser: boolean
  onEvaluationClick: (evaluationId: string) => void
  onMarkAsDemo: (evaluationId: string, currentlyDemo: boolean) => void
  onMarkAsActive: (evaluationId: string, clubId: string) => void
}

const SearchEvaluationItem = React.memo<EvaluationItemProps>(
  ({
    evaluation,
    club,
    isAdminUser,
    onEvaluationClick,
    onMarkAsDemo,
    onMarkAsActive,
  }) => {
    const axios = useAxios('/ratings/')
    const [isNavigateLoading, setIsNavigateLoading] = useState(false)
    const [isMarkAsDemoLoading, setIsMarkAsDemoLoading] = useState(false)
    const [isMarkAsActiveLoading, setIsMarkAsActiveLoading] = useState(false)
    const [confirmDialog, setConfirmDialog] = useState<{
      type: 'demo' | 'active'
    } | null>(null)

    const handleGoToCourse = async () => {
      /*    navigate('ClubManagementStack', {
        screen: 'ClubManagement',
        wait: true,
        params: {
          club: club.club,
        },
      })
*/
      if (!evaluation.holeIds) return

      const holeIds = evaluation.holeIds

      const templateBody = {
        courseId: evaluation.courseId,
        clubId: club.clubId,
        name: evaluation.courseName,
        holeIds,
        defaultCourseSettings: {
          bunkDepthDefault: 0,
          grassType: 'cool',
          intermediateCut: 0,
          measurement: 'imperial',
          roughHeight: 3,
          stimpFeet: 9,
          stimpInches: 0,
          'wind_1-9': 0,
          'wind_10-18': 0,
        },
        mapped: true,
      }

      console.log(holeIds)
      console.log(templateBody)

      let templateId: string
      try {
        const { data } = await axios.get<RatingTemplate>(
          `/templates?holeIds=${holeIds.join(',')}`
        )
        templateId = data.id
      } catch (error) {
        const { data } = await axios.post<RatingTemplate>(
          '/templates',
          templateBody
        )
        templateId = data.id
      }

      console.log({ templateId })
      /*      navigate('CreateRating', {
        holeIds,
        templateId,
        club: club.club,
        branchId: evaluation.branchId,
        isRateable: true,
      })*/

      navigate('ClubManagementStack', {
        screen: 'CreateRating',
        wait: false,
        params: {
          holeIds,
          templateId,
          club: club.club,
          branchId: club.branchId,
          isRateable: true,
        },
      })
    }

    const handleGoToEvaluation = (evaluation: Eval) => {
      if (!evaluation.id) {
        console.error('Evaluation ID is null')
        return
      }
      setIsNavigateLoading(true)
      onEvaluationClick(evaluation.id)
      setTimeout(() => {
        setIsNavigateLoading(false)
      }, 2000)
    }

    const handleMarkAsDemo = () => {
      if (!evaluation.id) return
      setConfirmDialog({ type: 'demo' })
    }

    const confirmMarkAsDemo = () => {
      if (!evaluation.id) return
      setConfirmDialog(null)
      setIsMarkAsDemoLoading(true)
      onMarkAsDemo(evaluation.id, !!evaluation.isDemoEvaluation)
      setTimeout(() => {
        setIsMarkAsDemoLoading(false)
      }, 2000)
    }

    const handleMarkAsActive = () => {
      if (!evaluation.id) return
      setConfirmDialog({ type: 'active' })
    }

    const confirmMarkAsActive = () => {
      if (!evaluation.id) return
      setConfirmDialog(null)
      setIsMarkAsActiveLoading(true)
      onMarkAsActive(evaluation.id, evaluation.clubId.toString())
      setTimeout(() => {
        setIsMarkAsActiveLoading(false)
      }, 2000)
    }

    return (
      <>
      <S.EvaluationItem key={evaluation.id} tabIndex={0}>
        <S.EvaluationMainContent>
          {evaluation.isActiveFinalized && <FaCheckCircle color={'#22c55e'} />}
          {evaluation.isDemoEvaluation && (
            <FaExclamationCircle color={'orange'} />
          )}
          <S.EvaluationTitle>
            {evaluation.saveName?.slice(0, 35) || '-'}
          </S.EvaluationTitle>
          <S.EvaluationMetaRow>
            <S.StatusBadge status={evaluation.status?.toString()}>
              {getStatusText(evaluation.status)}
            </S.StatusBadge>
            {evaluation.courseName && (
              <S.CourseBadge onClick={handleGoToCourse}>
                {evaluation.courseName}
              </S.CourseBadge>
            )}
            {evaluation.isDemoEvaluation && (
              <S.EvaluationMeta>
                <strong>Demo:</strong>
                <FaExclamationCircle color={'orange'} />
              </S.EvaluationMeta>
            )}
            {evaluation.evaluationDate && (
              <S.EvaluationMeta>
                <strong>Rated:</strong>
                {evaluation.evaluationDate
                  ? new Date(evaluation.evaluationDate)
                      .toISOString()
                      .split('T')[0]
                  : '-'}
              </S.EvaluationMeta>
            )}
            {evaluation.finalizedAt && (
              <S.EvaluationMeta>
                <strong>Finalized:</strong>
                {evaluation.finalizedAt
                  ? new Date(evaluation.finalizedAt).toISOString().split('T')[0]
                  : '-'}
              </S.EvaluationMeta>
            )}
            <S.ManualVersionBadge>
              Manual {evaluation.manualVersion ?? 'Unknown'}
            </S.ManualVersionBadge>
            <S.EvaluationMeta>
              <strong>ID:</strong> {evaluation.id}
            </S.EvaluationMeta>
          </S.EvaluationMetaRow>
          <S.ActionButtons>
            <S.PrimaryButton
              style={{
                backgroundColor: '#4190e0',
                marginLeft: 'auto',
              }}
              onClick={handleGoToCourse}
            >
              Manage ratings
            </S.PrimaryButton>

            <S.IconButton
              title="Go to Rating"
              onClick={() => handleGoToEvaluation(evaluation)}
              style={{
                backgroundColor: '#4190e0',
              }}
            >
              {isNavigateLoading ? (
                <ActivityIndicator size={14} color="white" />
              ) : (
                <FaFolderOpen />
              )}
            </S.IconButton>
            {isAdminUser && (
              <S.IconButton
                title={
                  evaluation.isDemoEvaluation
                    ? 'Unmark as DEMO'
                    : 'Mark as DEMO'
                }
                onClick={handleMarkAsDemo}
                style={{
                  backgroundColor: evaluation.isDemoEvaluation
                    ? 'white'
                    : '#f97316',
                }}
              >
                {isMarkAsDemoLoading ? (
                  <ActivityIndicator size={14} color="white" />
                ) : (
                  <FaExclamation
                    color={evaluation.isDemoEvaluation ? '#f97316' : 'white'}
                  />
                )}
              </S.IconButton>
            )}
            {evaluation.finalizedAt && !evaluation.isActiveFinalized && (
              <S.IconButton
                title="Mark as Active"
                onClick={handleMarkAsActive}
                style={{ backgroundColor: '#22c55e' }}
              >
                {isMarkAsActiveLoading ? (
                  <ActivityIndicator size={14} color="white" />
                ) : (
                  <FaCheckCircle />
                )}
              </S.IconButton>
            )}
          </S.ActionButtons>
        </S.EvaluationMainContent>
      </S.EvaluationItem>
      <ConfirmDialogV2
        open={confirmDialog?.type === 'demo'}
        title={evaluation.isDemoEvaluation ? 'Unmark as Demo' : 'Mark as Demo'}
        message={`Are you sure you want to ${evaluation.isDemoEvaluation ? 'unmark' : 'mark'} this evaluation as DEMO?`}
        confirmLabel={evaluation.isDemoEvaluation ? 'Unmark' : 'Mark as Demo'}
        variant={evaluation.isDemoEvaluation ? 'default' : 'danger'}
        onConfirm={confirmMarkAsDemo}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialogV2
        open={confirmDialog?.type === 'active'}
        title="Mark as Active"
        message="Are you sure you want to mark this evaluation as the Active finalized rating?"
        confirmLabel="Mark as Active"
        onConfirm={confirmMarkAsActive}
        onCancel={() => setConfirmDialog(null)}
      />
    </>
    )
  }
)

export default SearchEvaluationItem
